import { useQueryClient } from "@tanstack/react-query";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import { api, currentRefresh, currentToken, refreshSession, saveToken, setUnauthorizedHandler } from "./api";

const Ctx = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    (async () => {
      if (currentToken()) {
        try {
          setUser(await api("/auth/me"));
          refreshSession().catch(() => {});
        } catch {
          if (!currentToken()) setUser(null);
        }
      }
      setLoading(false);
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api("/auth/login", { method: "POST", body: { email, password } });
    saveToken(res.access_token, res.refresh_token, res.media_token);
    setUser(res.user);
  }, []);

  const logout = useCallback(() => {
    const rt = currentRefresh();
    if (rt) api("/auth/logout", { method: "POST", body: { refresh_token: rt } }).catch(() => {});
    saveToken(null);
    const branding = qc.getQueryData(["branding"]);
    qc.clear();
    if (branding) qc.setQueryData(["branding"], branding);
    setUser(null);
  }, [qc]);

  return React.createElement(Ctx.Provider, { value: { user, loading, login, logout } }, children);
}

export const useAuth = () => useContext(Ctx);
export const isManagerUser = (u) => u?.role === "admin" || u?.role === "supervisor";

export function usePerms() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManager = isManagerUser(user);
  return { isAdmin, isManager, isTech: !isManager, seeMoney: isManager };
}
